package com.macrofocus.trenddisplay.timeseriesplot

import org.mkui.canvas.*
import org.mkui.geom.Point
import org.mkui.geom.Rectangle2D
import org.molap.aggregates.cube.Group
import kotlin.math.max
import kotlin.math.min

abstract class AbstractTimeSeriesPlotController<Row, Column>(view: TimeSeriesPlotView<Row, Column>?) :
    TimeSeriesPlotController<Row, Column> {
    private var view: TimeSeriesPlotView<Row, Column>? = null

    /**
     * Interaction mode.
     */
    enum class Mode {
        /**
         * Selection mode.
         */
        Selection,

        /**
         * Zooming mode.
         */
        Zooming,

        /**
         * Panning mode.
         */
        Panning
    }

    private var mode = Mode.Selection

    private val multipleSelectionEnabled = true
    private val selectOnPopupTrigger = false

    private val improvedBorderZooming = false

    private val mouseListener: DefaultMouseListener = DefaultMouseListener()

    private val keyListener: KeyListener = object : KeyListener {
        var oldMode: Mode? = null

        override fun keyTyped(event: KeyEvent) {
        }

        override fun keyPressed(event: KeyEvent) {
            if (event.keyCode === KeyEvent.VK_S) {
                if (oldMode == null) {
                    oldMode = mode
                }
                mode = Mode.Selection
            }
            if (event.keyCode === KeyEvent.VK_Z ||
                event.keyCode === KeyEvent.VK_SHIFT
            ) {
                if (oldMode == null) {
                    oldMode = mode
                }
                mode = Mode.Zooming
            }
            if (event.keyCode === KeyEvent.VK_P ||
                event.keyCode === KeyEvent.VK_SPACE
            ) {
                if (oldMode == null) {
                    oldMode = mode
                }
                mode = Mode.Panning
            }
        }

        override fun keyReleased(event: KeyEvent) {
            if (oldMode != null) {
                mode = oldMode!!
                oldMode = null
            }
        }
    }


    init {
        setView(view)
    }

    override fun setView(view: TimeSeriesPlotView<Row, Column>?) {
        if (this.view != null) {
//            this.view.removeMouseListener(mouseListener);
//            this.view.removeMouseMotionListener(mouseListener);
        }

        this.view = view

        if (view != null) {
            view.addKeyListener(keyListener)
            view.addMouseListener(mouseListener)
            view.addMouseMotionListener(mouseListener)
            view.addMouseWheelListener(mouseListener)
        }
    }

    private fun zoom(amount: Double, xCenter: Double, yCenter: Double) {
        val viewport: Rectangle2D = viewport
        val world: Rectangle2D = world

        //        if (improvedBorderZooming) {
//            final double normalizedXCenter = normalize(xCenter, world.getMinX(), world.getMaxX());
//            final double normalizedYCenter = normalize(yCenter, world.minY, world.getMaxY());
//
//            xCenter = denormalize(zoomingCenterFunction(normalizedXCenter), world.getMinX(), world.getMaxX());
//            yCenter = denormalize(zoomingCenterFunction(normalizedYCenter), world.minY, world.getMaxY());
//        }
//
        val xRange: Range = Range(
            amount,
            xCenter,
            viewport.minX,
            viewport.width,
            world.minX,
            world.maxX,
            view!!.xRangeModel!!.maximumExtent
        ).invoke()
        val xMin: Double = xRange.min
        val xMax: Double = xRange.max

        val yRange: Range = Range(
            amount,
            yCenter,
            viewport.minY,
            viewport.height,
            world.minY,
            world.maxY,
            view!!.yRangeModel!!.maximumExtent
        ).invoke()
        val yMin: Double = yRange.min
        val yMax: Double = yRange.max

        if (xMin < xMax && yMin < yMax) {
            view?.zoom(false, xMin, xMax, yMin, yMax)
        }
    }

    val viewport: Rectangle2D
        get() {
            val r: Rectangle2D.Double = Rectangle2D.Double(
                view!!.xRangeModel!!.start,
                view!!.yRangeModel!!.start,
                view!!.xRangeModel!!.extent,
                view!!.yRangeModel!!.extent
            )
            return r
        }

    val world: Rectangle2D
        get() {
            val r: Rectangle2D.Double = Rectangle2D.Double(
                view!!.xRangeModel!!.minimum,
                view!!.yRangeModel!!.minimum,
                view!!.xRangeModel!!.maximum - view!!.xRangeModel!!.minimum,
                view!!.yRangeModel!!.maximum - view!!.yRangeModel!!.minimum
            )
            return r
        }

    private inner class Range(
        private val _amount: Double,
        private val _center: Double?,
        private val _start: Double,
        private val _extent: Double,
        private val _minRange: Double,
        private val _maxRange: Double,
        private val _maxExtent: Double
    ) {
        var min: Double = 0.0
            private set
        var max: Double = 0.0
            private set

        fun invoke(): Range {
            val range = _extent
            val newRange = min(_maxExtent, range + (range * (_amount)))

            if (_center != null) {
                min = _center - (newRange * ((_center - _start) / range))
                max = _center + (newRange * (((_start + _extent) - _center) / range))
            } else {
                min = _start
                max = _start + newRange
            }

            if (min < _minRange) {
                max = max + (_minRange - min)
                min = _minRange
            }
            if (max > _maxRange) {
                min = min - (max - _maxRange)
                max = _maxRange
            }
            min = max(_minRange, min)
            max = min(_maxRange, max)
            return this
        }
    }

    inner class DefaultMouseListener : MouseListener, MouseMotionListener,
        MouseWheelListener {
        private var last: MouseEvent? = null
        private var selectOnRelease: Row? = null
        private var zoomOnRelease: Point? = null
        private val zoomingPoint: Point? = null
        private var pressedPoint: Point? = null

        override fun mouseClicked(event: MouseEvent) {
        }

        override fun mousePressed(event: MouseEvent) {
            if (view != null) {
                pressedPoint = Point(event.x, event.y)

                val bestrow: Row? = view!!.getClosestRow(event.x, event.y)
                if (bestrow != null) {
                    if (!event.isMultipleSelectionKey) {
                        if (!view!!.model!!.selection!!.isSelected(bestrow) ||
                            view!!.model!!.selection!!.selectedCount > 1
                        ) {
                            view!!.model!!.selection!!.clearSelection()
                            view!!.model!!.selection!!.setSelectedElement(bestrow)
                            view!!.isSelectionMode = view!!.model!!.selection!!.isSelected(bestrow)
                        }
                    } else {
                        view!!.isSelectionMode = !view!!.model!!.selection!!.isSelected(bestrow)
                        if (view!!.isSelectionMode) {
                            view!!.model!!.selection!!.setSelectedState(bestrow, true)
                        } else {
                            view!!.model!!.selection!!.setSelectedState(bestrow, false)
                        }
                    }
                } else {
                    view!!.model!!.selection!!.clearSelection()
                }
            }
        }

        override fun mouseReleased(event: MouseEvent) {
            if (view != null) {
                if (!event.isPopupTrigger) {
                    if (selectOnRelease != null) {
                        if (event.isLeftMouseButton) {
                            view!!.model!!.selection!!.clearSelection()
                            if (multipleSelectionEnabled) {
                                addToSelection(selectOnRelease!!)
                            } else {
                                setSelection(selectOnRelease)
                            }
                            selectOnRelease = null
                        }
                    }
                    if (zoomOnRelease != null) {
                        if (event.isLeftMouseButton) {
//                            zoom(-1.0 / 20.0, _view.screenToWorldX(zoomOnRelease.x), _view.screenToWorldY(zoomOnRelease.y));
                        } else {
//                            zoom(1.0 / 20.0, _view.screenToWorldX(zoomOnRelease.x), _view.screenToWorldY(zoomOnRelease.y));
                        }
                        zoomOnRelease = null
                    }

                    view!!.rubberBand?.stopRubberBand()
                } else {
                    if (selectOnPopupTrigger) {
                        val node: Row? = view!!.getClosestRow(event.x, event.y)
                        setSelection(node)
                    }

                    //                    if (popupMenu != null) {
//                        popupMenu.show(_view, e.getX(), e.y);
//                    }
                }

                last = null
            }
        }

        override fun mouseEntered(event: MouseEvent) {
            if (view != null) {
                if (view!!.model != null) {
                    view!!.model!!.probing!!.selected = view!!.getClosestRow(event.x, event.y)
                }
            }
        }

        override fun mouseExited(event: MouseEvent) {
            if (view != null) {
                if (view!!.model != null) {
                    view!!.model!!.probing!!.clearSelection()
                }
            }
        }

        override fun mouseDragged(event: MouseEvent) {
            if (view != null) {
                if (view!!.model != null) {
                    if (last != null) {
                        when (mode) {
                            Mode.Selection -> if (event.isLeftMouseButton) {
                                if (event.isAltKeyDown || event.isShiftKeyDown) {
                                    view!!.model!!.probing!!.clearSelection()
                                    if (multipleSelectionEnabled) {
                                        if (!view!!.rubberBand!!.rubberBand.isActive) {
                                            view!!.rubberBand!!.startRubberBand(pressedPoint!!.x.toInt(), pressedPoint!!.y.toInt())
                                        }
                                        view!!.rubberBand!!.stretchRubberBand(event.x, event.y)
                                        val rect: Rectangle2D? = view?.rubberBand?.rubberBandScreen
                                        if (rect != null) {
                                            val toSelect: List<Row> = view!!.getRows(rect)
                                            if (event.isMultipleSelectionKey) {
                                                addToSelection(toSelect)
                                            } else {
                                                view!!.model!!.selection!!.setSelectedIterable(toSelect)
                                            }
                                            selectOnRelease = null
                                        }
                                    }
                                } else {
                                    val observation: Row? = view!!.getClosestRow(event.x, event.y)
                                    view!!.model!!.probing!!.selected = observation

                                    selectOnRelease = null

                                    val isAlreadySelected =
                                        view!!.model!!.selection != null && view!!.model!!.selection!!.isSelected(observation)

                                    if (!isAlreadySelected && !event.isMenuShortcutKeyDown) {
                                        view!!.model!!.selection!!.clearSelection()
                                    }
                                    if (event.isMenuShortcutKeyDown) {
                                        if (multipleSelectionEnabled) {
                                            addToSelection(observation!!)
                                        } else {
                                            setSelection(observation)
                                        }
                                    } else {
                                        if (isAlreadySelected) {
                                            selectOnRelease = observation
                                        } else {
                                            if (multipleSelectionEnabled) {
                                                addToSelection(observation!!)
                                            } else {
                                                setSelection(observation)
                                            }
                                        }
                                    }
                                }
                            }

                            Mode.Panning -> if (event.isLeftMouseButton) {
                                val x1: Double = view!!.screenTransform!!.x.screenToWorld(last!!.x)
                                val x2: Double = view!!.screenTransform!!.x.screenToWorld(event.x)
                                val worldDiffX = x1 - x2
                                var xMin: Double = viewport.minX + worldDiffX
                                var xMax: Double = viewport.maxX + worldDiffX

                                val y1: Double = view!!.screenTransform!!.y.screenToWorld(last!!.y)
                                val y2: Double = view!!.screenTransform!!.y.screenToWorld(event.y)
                                val worldDiffY = y1 - y2
                                var yMin: Double = viewport.minY + worldDiffY
                                var yMax: Double = viewport.maxY + worldDiffY

                                if (xMin < world.minX) {
                                    xMax = xMax + (world.minX - xMin)
                                    xMin = world.minX
                                }
                                if (xMax > world.maxX) {
                                    xMin = xMin - (xMax - world.maxX)
                                    xMax = world.maxX
                                }

                                xMin = max(world.minX, xMin)
                                xMax = min(world.maxX, xMax)

                                if (yMin < world.minY) {
                                    yMax = yMax + (world.minY - yMin)
                                    yMin = world.minY
                                }
                                if (yMax > world.maxY) {
                                    yMin = yMin - (yMax - world.maxY)
                                    yMax = world.maxY
                                }

                                yMin = max(world.minY, yMin)
                                yMax = min(world.maxY, yMax)

                                if (xMin < xMax && yMin < yMax) {
                                    view?.zoom(false, xMin, xMax, yMin, yMax)
                                }
                            }

                            Mode.Zooming -> if (event.isMiddleMouseButton) {
                                zoom(
                                    event.y - last!!.y / 100.0,
                                    view!!.screenTransform!!.x.screenToWorld(event.x),
                                    view!!.screenTransform!!.y.screenToWorld(event.y)
                                )
                                zoomOnRelease = null
                            }
                        }
                    }
                    last = event
                }
            }
        }

        override fun mouseMoved(event: MouseEvent) {
//            val closest: Row? = view!!.getClosestRow(event.x, event.y)
//            view!!.model!!.probing!!.selected = closest
            val closest: Group? = view!!.getClosestGroup(event.x, event.y)
            view!!.model!!.masterVisual!!.probing!!.selected = closest
        }

        override fun mouseWheelMoved(event: MouseWheelEvent) {
            if (view != null) {
                val amount: Double = event.wheelRotation
                zoom(
                    amount / 20.0, view!!.screenTransform!!.x.screenToWorld(event.x),
                    view!!.screenTransform!!.y.screenToWorld(event.y)
                )
            }
        }

        private fun removeFromSelection(observation: Row) {
            view!!.model!!.selection!!.setSelectedState(observation, false)
        }

        private fun setSelection(observation: Row?) {
            if (observation != null) {
                view!!.model!!.selection!!.setSelectedElements(observation)
            } else {
                view!!.model!!.selection!!.clearSelection()
            }
        }

        private fun addToSelection(observation: Row) {
            view!!.model!!.selection!!.setSelectedState(observation, true)
        }

        private fun addToSelection(newSelection: List<Row>) {
            view!!.model!!.selection!!.setSelectedIterableState(newSelection, true)
        }
    }
}